import { useCallback, useMemo } from 'react';
import { pick } from 'lodash';

import { ensureDefined } from '@e-socna/utils';
import { getFragmentData, graphql } from '@e-socna/data-layer';
import { EMPTY_ARRAY } from '@e-socna/empty';
import type { ReportWorkflowId, ReportStateId, Id } from '@e-socna/data-layer';

import { useReportInfoAccess } from '../../components/Access/hooks/useReportInfoAccess.hook';
import { FolderBusinessEntityState } from '../../components/FolderBusinessEntityState';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useMissionReadyContext } from '../../contexts/MissionContext/hooks/useMissionReadyContext.hook';
import { useFindAllDocumentTemplatesQuery } from '../../pages/DocumentAndSectionTemplates/hooks/useFindAllDocumentTemplatesQuery.hook';
import type { MissionReportPage_ReportInfo } from '../../pages/MissionReport/fragments/report-info.fragment';

import type { RenderActionDialog } from '../FolderBusinessEntityState/types/action-dialog-render-props.type';

import { singleTemplateIdOf } from '../DocumentTemplates/utils/single-template-id.util';

import { ReportDocumentChoiceFields } from './DocumentChoiceFields';
import { useReportCodeSuggestionFactory } from './hooks/useReportCodeSuggestion.hook';
import { useFindAllMissionReportInfoActionPermissionsQuery } from './hooks/useFindAllMissionReportInfoActionPermissionsQuery.hook';
import { useUpdateMissionReportInfoStateSubmitFactory } from './hooks/useUpdateMissionReportInfoStateSubmitFactory.hook';

export const MissionReportInfoStateForm_ResolvableMissionType_Fragment =
  graphql(`
    fragment MissionReportInfoStateForm_ResolvableMissionType_Fragment on ResolvableMissionType {
      reportTemplateIds
    }
  `);

interface Props {
  readonly enterEditionMode: () => void;
  readonly isInEditionMode: boolean;
  readonly leaveEditionMode: () => void;
  readonly missionId: Id;
  readonly missionReportInfo: MissionReportPage_ReportInfo;
  readonly onSuccess?: (params: { actionId: Id; newStateId: Id }) => void;
  readonly reportId: Id;
  readonly showSummary?: boolean;
}

export const MissionReportInfoStateForm = ({
  enterEditionMode,
  isInEditionMode,
  leaveEditionMode,
  missionId,
  missionReportInfo,
  onSuccess,
  reportId,
  showSummary,
}: Readonly<Props>) => {
  const {
    genericStatusesCodeMap,
    documentTypesCodeMap,
    reportStatesIdMap,
    reportWorkflowsIdMap,
  } = useReferencesContext();

  const { mission, resolvableMissionType } = useMissionReadyContext();

  const { reportState, reportWorkflow } = ensureDefined({
    reportWorkflow:
      reportWorkflowsIdMap[missionReportInfo.workflowId as ReportWorkflowId],
    reportState: reportStatesIdMap[missionReportInfo.stateId as ReportStateId],
  });

  const { currentNode } = ensureDefined(
    {
      currentNode: reportWorkflow.stateMachine.find(
        (node) => node.stateId === reportState._id,
      ),
    },
    'Current node not found',
  );

  const { data, isFetching } =
    useFindAllMissionReportInfoActionPermissionsQuery(
      missionId,
      missionReportInfo._id,
      isInEditionMode,
    );

  const { data: templatesData } = useFindAllDocumentTemplatesQuery();

  const reportTemplates = useMemo(
    () =>
      (templatesData?.findAllDocumentTemplates ?? EMPTY_ARRAY).filter(
        (template) =>
          template.statusId === genericStatusesCodeMap.Enabled._id &&
          template.document.typeId === documentTypesCodeMap.Report._id,
      ),
    [
      documentTypesCodeMap.Report._id,
      genericStatusesCodeMap.Enabled._id,
      templatesData,
    ],
  );

  const reportTemplateIds = getFragmentData(
    MissionReportInfoStateForm_ResolvableMissionType_Fragment,
    resolvableMissionType,
  )?.reportTemplateIds;

  const suggestedTemplateIds = useMemo(
    () => new Set<Id>(reportTemplateIds ?? EMPTY_ARRAY),
    [reportTemplateIds],
  );

  const useCodeSuggestion = useReportCodeSuggestionFactory({
    missionId,
    workflowId: missionReportInfo.workflowId,
  });

  const useUpdateMissionReportInfoStateSubmit =
    useUpdateMissionReportInfoStateSubmitFactory({
      missionId,
      reportId,
      reportInfoId: missionReportInfo._id,
      onSuccess,
    });

  const documentAlreadyExists = !!missionReportInfo.reportId;

  const currentNodeActions = currentNode.actions;
  const missionTitle = mission.consolidation.title;

  const renderActionDialog: RenderActionDialog = useCallback(
    (formikBag) => {
      if (documentAlreadyExists) {
        return null;
      }

      const selectedAction = currentNodeActions.find(
        (action) => action._id === formikBag.values.actionId,
      );

      if (!selectedAction?.settings.autoCreateDocumentIfNotExists) {
        return null;
      }

      return {
        title: 'Document du rapport',
        // eslint-disable-next-line dot-notation -- index signature access required by noPropertyAccessFromIndexSignature
        isValid: !!formikBag.values['title'],
        content: (
          <ReportDocumentChoiceFields
            defaultTemplateId={singleTemplateIdOf(reportTemplateIds)}
            defaultTitle={missionTitle}
            key={selectedAction._id}
            reportTemplates={reportTemplates}
            suggestedTemplateIds={suggestedTemplateIds}
            useCodeSuggestion={useCodeSuggestion}
          />
        ),
      };
    },
    [
      currentNodeActions,
      documentAlreadyExists,
      missionTitle,
      reportTemplateIds,
      reportTemplates,
      suggestedTemplateIds,
      useCodeSuggestion,
    ],
  );

  const currentNodeWithReportState = useMemo(
    () => ({
      ...currentNode,
      ...pick(reportState, ['label', 'description', 'chip']),
    }),
    [currentNode, reportState],
  );

  const { changeState } = useReportInfoAccess(
    missionId,
    missionReportInfo._id,
    missionReportInfo,
  );

  return (
    <FolderBusinessEntityState
      actionPermissions={data?.findOneMission.access.actions}
      areActionPermissionsLoading={isFetching}
      currentNode={currentNodeWithReportState}
      enableAction
      formTitle="Mise à jour du Statut du Rapport de Mission"
      gate={changeState}
      isInEditionMode={isInEditionMode}
      onCancel={leaveEditionMode}
      onEdit={enterEditionMode}
      renderActionDialog={renderActionDialog}
      showSummary={showSummary}
      useUpdateStateSubmit={useUpdateMissionReportInfoStateSubmit}
    />
  );
};
